if (!customElements.get('media-gallery')) {
  customElements.define(
    'media-gallery',
    class MediaGallery extends HTMLElement {
      constructor() {
        super();
        this.elements = {
          liveRegion: this.querySelector('[id^="GalleryStatus"]'),
          viewer: this.querySelector('[id^="GalleryViewer"]'),
          thumbnails: this.querySelector('[id^="GalleryThumbnails"]'),
        };
        this.mql = window.matchMedia('(min-width: 750px)');

        if (this.elements.thumbnails) {
          this.elements.viewer?.addEventListener('slideChanged', debounce(this.onSlideChanged.bind(this), 500));
          this.elements.thumbnails.querySelectorAll('[data-target]').forEach((mediaToSwitch) => {
            mediaToSwitch
              .querySelector('button')
              ?.addEventListener('click', this.setActiveMedia.bind(this, mediaToSwitch.dataset.target, false));
          });
          if (this.dataset.desktopLayout?.includes('thumbnail') && this.mql.matches) this.removeListSemantic();
        }
      }

      connectedCallback() {
        if (document.readyState === 'loading') {
          document.addEventListener('DOMContentLoaded', () => this.initHausielVariantGallery(), { once: true });
        } else {
          this.initHausielVariantGallery();
        }
      }

      disconnectedCallback() {
        if (typeof this.hausielVariantUnsubscriber === 'function') this.hausielVariantUnsubscriber();
      }

      initHausielVariantGallery() {
        if (this.hausielVariantGalleryReady) return;
        const payloadNode = document.querySelector('[data-hausiel-variant-media-map]');
        if (!payloadNode) return;

        try {
          const payload = JSON.parse(payloadNode.textContent || '{}');
          if (!payload.map || typeof payload.map !== 'object') return;
          this.hausielVariantMediaMap = payload.map;
          this.hausielAllVariantMediaIds = new Set(Object.values(payload.map).flat().map(String));
          this.hausielVariantGalleryReady = true;
          this.applyHausielVariantMedia(String(payload.initialVariantId || ''));

          if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
            this.hausielVariantUnsubscriber = subscribe(PUB_SUB_EVENTS.variantChange, (event) => {
              const variant = event?.data?.variant;
              const variantId = variant?.id != null ? String(variant.id) : '';
              if (!variantId || !this.hausielVariantMediaMap[variantId]) return;
              window.requestAnimationFrame(() => this.applyHausielVariantMedia(variantId));
            });
          }
        } catch (error) {
          console.warn('HAUSIEL variant gallery map could not be parsed.', error);
        }
      }

      extractHausielMediaId(value) {
        if (!value) return '';
        const parts = String(value).split('-');
        return parts[parts.length - 1];
      }

      applyHausielVariantMedia(variantId) {
        const selectedIds = new Set((this.hausielVariantMediaMap?.[String(variantId)] || []).map(String));
        if (!selectedIds.size || !this.elements.viewer) return;

        const slides = Array.from(this.elements.viewer.querySelectorAll('[data-media-id]'));
        slides.forEach((slide) => {
          const mediaId = this.extractHausielMediaId(slide.dataset.mediaId);
          const shouldHide = this.hausielAllVariantMediaIds.has(mediaId) && !selectedIds.has(mediaId);
          slide.hidden = shouldHide;
          slide.setAttribute('aria-hidden', shouldHide ? 'true' : 'false');
        });

        if (this.elements.thumbnails) {
          this.elements.thumbnails.querySelectorAll('[data-target]').forEach((thumbnail) => {
            const mediaId = this.extractHausielMediaId(thumbnail.dataset.target);
            const shouldHide = this.hausielAllVariantMediaIds.has(mediaId) && !selectedIds.has(mediaId);
            thumbnail.hidden = shouldHide;
            thumbnail.setAttribute('aria-hidden', shouldHide ? 'true' : 'false');
          });
        }

        const activeMedia = this.elements.viewer.querySelector('.is-active[data-media-id]');
        if (!activeMedia || activeMedia.hidden) {
          const firstVisibleVariantMedia = slides.find((slide) => {
            const mediaId = this.extractHausielMediaId(slide.dataset.mediaId);
            return !slide.hidden && selectedIds.has(mediaId);
          });
          const firstVisibleMedia = firstVisibleVariantMedia || slides.find((slide) => !slide.hidden);
          if (firstVisibleMedia) this.setActiveMedia(firstVisibleMedia.dataset.mediaId, false);
        }

        this.elements.viewer.resetPages?.();
        this.elements.thumbnails?.resetPages?.();

        const visibleCount = slides.filter((slide) => !slide.hidden).length;
        const total = this.querySelector('.slider-counter--total');
        if (total && visibleCount > 0) total.textContent = String(visibleCount);
      }

      onSlideChanged(event) {
        if (!this.elements.thumbnails) return;
        const thumbnail = this.elements.thumbnails.querySelector(
          `[data-target="${event.detail.currentElement.dataset.mediaId}"]`
        );
        this.setActiveThumbnail(thumbnail);
      }

      setActiveMedia(mediaId, prepend) {
        const activeMedia =
          this.elements.viewer?.querySelector(`[data-media-id="${mediaId}"]:not([hidden])`) ||
          this.elements.viewer?.querySelector('[data-media-id]:not([hidden])') ||
          this.elements.viewer?.querySelector('[data-media-id]');
        if (!activeMedia) return;

        this.elements.viewer.querySelectorAll('[data-media-id]').forEach((element) => {
          element.classList.remove('is-active');
        });
        activeMedia.classList.add('is-active');

        if (prepend) {
          activeMedia.parentElement.firstChild !== activeMedia && activeMedia.parentElement.prepend(activeMedia);

          if (this.elements.thumbnails) {
            const activeThumbnail = this.elements.thumbnails.querySelector(`[data-target="${activeMedia.dataset.mediaId}"]`);
            if (activeThumbnail && activeThumbnail.parentElement.firstChild !== activeThumbnail) {
              activeThumbnail.parentElement.prepend(activeThumbnail);
            }
          }

          this.elements.viewer.resetPages?.();
        }

        this.preventStickyHeader();
        window.setTimeout(() => {
          if (!this.mql.matches || this.elements.thumbnails) {
            activeMedia.parentElement.scrollTo({ left: activeMedia.offsetLeft });
          }
          const activeMediaRect = activeMedia.getBoundingClientRect();
          if (activeMediaRect.top > -0.5) return;
          const top = activeMediaRect.top + window.scrollY;
          window.scrollTo({ top, behavior: 'smooth' });
        });
        this.playActiveMedia(activeMedia);

        if (!this.elements.thumbnails) return;
        const activeThumbnail = this.elements.thumbnails.querySelector(`[data-target="${activeMedia.dataset.mediaId}"]`);
        this.setActiveThumbnail(activeThumbnail);
        if (activeThumbnail) this.announceLiveRegion(activeMedia, activeThumbnail.dataset.mediaPosition);
      }

      setActiveThumbnail(thumbnail) {
        if (!this.elements.thumbnails || !thumbnail) return;

        this.elements.thumbnails
          .querySelectorAll('button')
          .forEach((element) => element.removeAttribute('aria-current'));
        thumbnail.querySelector('button')?.setAttribute('aria-current', true);
        if (this.elements.thumbnails.isSlideVisible?.(thumbnail, 10)) return;

        this.elements.thumbnails.slider?.scrollTo({ left: thumbnail.offsetLeft });
      }

      announceLiveRegion(activeItem, position) {
        if (!this.elements.liveRegion) return;
        const image = activeItem.querySelector('.product__modal-opener--image img');
        if (!image) return;
        image.onload = () => {
          this.elements.liveRegion.setAttribute('aria-hidden', false);
          this.elements.liveRegion.innerHTML = window.accessibilityStrings.imageAvailable.replace('[index]', position);
          setTimeout(() => {
            this.elements.liveRegion.setAttribute('aria-hidden', true);
          }, 2000);
        };
        image.src = image.src;
      }

      playActiveMedia(activeItem) {
        window.pauseAllMedia();
        const deferredMedia = activeItem.querySelector('.deferred-media');
        if (deferredMedia) deferredMedia.loadContent(false);
      }

      preventStickyHeader() {
        this.stickyHeader = this.stickyHeader || document.querySelector('sticky-header');
        if (!this.stickyHeader) return;
        this.stickyHeader.dispatchEvent(new Event('preventHeaderReveal'));
      }

      removeListSemantic() {
        if (!this.elements.viewer?.slider) return;
        this.elements.viewer.slider.setAttribute('role', 'presentation');
        this.elements.viewer.sliderItems.forEach((slide) => slide.setAttribute('role', 'presentation'));
      }
    }
  );
}
